const LABEL_ATTRS = ['data-label-desktop', 'data-label-mobile', 'data-anchor'];

class ThemeTabs extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.tabs__bar');
    this.list = this.querySelector('[role="tablist"]');
    this.panelsEl = this.querySelector('.tabs__panels');
    if (!this.list || !this.panelsEl) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleMutation = this.handleMutation.bind(this);

    this.list.addEventListener('click', this.handleClick);
    this.list.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    // The theme editor swaps block markup without reconnecting this element,
    // so watch for panels being added, removed, or relabelled.
    this.observer = new MutationObserver(this.handleMutation);
    this.observer.observe(this.panelsEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: LABEL_ATTRS,
    });

    this.build();

    const fromHash = this.indexFromHash();
    this.activate(fromHash > -1 ? fromHash : 0, { scrollTab: fromHash > -1 });

    if (fromHash > -1) {
      requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.list?.removeEventListener('click', this.handleClick);
    this.list?.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.observer?.disconnect();
    cancelAnimationFrame(this.rebuildFrame);
  }

  get deepLinkEnabled() {
    return this.dataset.deepLink === 'true';
  }

  /* ---- Build the tab bar from the panels ----------------------------- */

  build() {
    // Panels are matched anywhere in the subtree: Shopify may wrap each
    // theme block, so direct-child queries are unreliable.
    this.panels = Array.from(
      this.panelsEl.querySelectorAll('[role="tabpanel"]')
    ).filter((panel) => panel.closest('theme-tabs') === this);

    this.list.textContent = '';

    this.tabs = this.panels.map((panel) => {
      const desktop = panel.dataset.labelDesktop || 'Tab';
      const mobile = panel.dataset.labelMobile || desktop;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `tab-${panel.dataset.tabId}`;
      tab.setAttribute('aria-controls', panel.id);
      tab.tabIndex = -1;
      if (panel.dataset.anchor) tab.dataset.anchor = panel.dataset.anchor;

      // textContent, not innerHTML — labels are merchant input.
      const desktopLabel = document.createElement('span');
      desktopLabel.className = 'tabs__label tabs__label--desktop';
      desktopLabel.textContent = desktop;

      const mobileLabel = document.createElement('span');
      mobileLabel.className = 'tabs__label tabs__label--mobile';
      mobileLabel.textContent = mobile;

      tab.append(desktopLabel, mobileLabel);

      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;

      return tab;
    });

    this.list.append(...this.tabs);
    this.bar.hidden = this.tabs.length === 0;
  }

  handleMutation(records) {
    const relevant = records.some((record) => {
      if (record.type === 'attributes') {
        return record.target.matches?.('[role="tabpanel"]');
      }
      // childList: only care when a panel itself enters or leaves the DOM,
      // directly or inside a wrapper the editor replaced. Nested block edits
      // inside a surviving panel don't need a rebuild.
      if (record.target === this.panelsEl) return true;
      const nodes = [...record.addedNodes, ...record.removedNodes];
      return nodes.some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[role="tabpanel"]') ||
            node.querySelector?.('[role="tabpanel"]'))
      );
    });
    if (!relevant) return;

    cancelAnimationFrame(this.rebuildFrame);
    this.rebuildFrame = requestAnimationFrame(() => {
      const previous = this.tabs?.findIndex(
        (tab) => tab.getAttribute('aria-selected') === 'true'
      );
      this.build();
      const next = Math.min(Math.max(previous ?? 0, 0), this.tabs.length - 1);
      this.activate(next);
    });
  }

  /* ---- Interaction --------------------------------------------------- */

  indexFromHash() {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (!hash) return -1;
    return this.tabs.findIndex((tab) => tab.dataset.anchor === hash);
  }

  handleClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.tabs.includes(tab)) return;
    this.activate(this.tabs.indexOf(tab), {
      focus: true,
      scrollTab: true,
      updateHash: true,
    });
  }

  handleKeydown(event) {
    const current = this.tabs.indexOf(document.activeElement);
    if (current === -1) return;

    const last = this.tabs.length - 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = rtl ? current - 1 : current + 1;
        break;
      case 'ArrowLeft':
        next = rtl ? current + 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    if (next < 0) next = last;
    if (next > last) next = 0;
    this.activate(next, { focus: true, scrollTab: true, updateHash: true });
  }

  // Theme editor: selecting a nested block inside a hidden panel must reveal it.
  handleBlockSelect(event) {
    const panel = event.target?.closest?.('[role="tabpanel"]');
    if (!panel || !this.contains(panel)) return;
    const index = this.panels.indexOf(panel);
    if (index > -1) this.activate(index, { scrollTab: true });
  }

  activate(index, { focus = false, scrollTab = false, updateHash = false } = {}) {
    const tab = this.tabs?.[index];
    if (!tab) return;

    this.tabs.forEach((item, i) => {
      const selected = i === index;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = this.panels[i];
      if (panel) panel.hidden = !selected;
    });

    if (focus) tab.focus();
    if (scrollTab) this.scrollTabIntoView(tab);

    if (updateHash && this.deepLinkEnabled && tab.dataset.anchor) {
      // replaceState avoids a browser jump to a non-existent #id target.
      history.replaceState(null, '', `#${tab.dataset.anchor}`);
    }

    this.dispatchEvent(
      new CustomEvent('tabs:change', {
        bubbles: true,
        detail: { index, tab, panel: this.panels[index] },
      })
    );
  }

  // Scroll the tab strip only — never the page.
  scrollTabIntoView(tab) {
    const list = this.list;
    if (list.scrollWidth <= list.clientWidth) return;
    const target = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('theme-tabs')) {
  customElements.define('theme-tabs', ThemeTabs);
}